import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import VisibilityIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffRounded";
import CameraAltRoundedIcon from "@mui/icons-material/CameraAltRounded";
import CropRoundedIcon from "@mui/icons-material/CropRounded";
import {
  EmailAuthProvider,
  getAuth,
  reauthenticateWithCredential,
  verifyBeforeUpdateEmail,
} from "firebase/auth";
import Overlay from "components/Overlay/Overlay";
import { useStateContext } from "contexts/ContextProvider";
import { usePageTitle } from "hooks/usePageTitle";
import linguistApi from "services/linguistApi";
import { parsePhoneNumber } from "libphonenumber-js";
import Cropper from "react-easy-crop";
import { countryOptions } from "constants/countryData";
import { getAvatarColor, getAvatarInitials } from "helpers/avatarHelpers";
import {
  getCroppedImg,
  createThumbnail,
  blobToBase64,
} from "helpers/imageUtils";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const LinguistProfileEditOverlay = () => {
  usePageTitle("Edit Profile");
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { overlayData, setOverlayName, showSnackbar, triggerPageRefresh } = useStateContext();

  const [name, setName] = useState(overlayData?.profile?.Name || "");
  const [email] = useState(overlayData?.profile?.Email || "");
  const initPhone = () => {
    const phones = overlayData?.profile?.PhoneNumbers || [];
    const primary = phones.find((p) => p.IsPrimary) || phones[0];
    if (primary) {
      // PhoneNumbers[].CountryCode stores the CALLING code (e.g. "1").
      // Map it back to the ISO country used by the flag picker.
      const matchedCountry = countryOptions.find(
        (c) => String(c.callingCode) === String(primary.CountryCode),
      );
      return {
        countryCode: matchedCountry?.code || "US",
        number: primary.Number || "",
        ext: primary.Ext || "",
      };
    }
    return { countryCode: "US", number: "", ext: "" };
  };
  const [phoneCountryCode, setPhoneCountryCode] = useState(initPhone().countryCode);
  const [phoneNumber, setPhoneNumber] = useState(initPhone().number);
  const [phoneExt, setPhoneExt] = useState(initPhone().ext);
  const [addressLine1, setAddressLine1] = useState(
    overlayData?.profile?.AddressLine1 || "",
  );
  const [addressLine2, setAddressLine2] = useState(
    overlayData?.profile?.AddressLine2 || "",
  );
  const [addressLine3, setAddressLine3] = useState(
    overlayData?.profile?.AddressLine3 || "",
  );
  const [certifications, setCertifications] = useState(
    overlayData?.profile?.Certifications || "",
  );
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(
    overlayData?.profile?.Image || overlayData?.profile?.ImageThumbnail || null,
  );
  const [imageToCrop, setImageToCrop] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [emailChangeOpen, setEmailChangeOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailChangePassword, setEmailChangePassword] = useState("");
  const [showEmailChangePassword, setShowEmailChangePassword] = useState(false);
  const [isSendingVerification, setIsSendingVerification] = useState(false);
  const [newEmailError, setNewEmailError] = useState("");
  // Pending-verification hint (Y-E5) — local only, cleared on discard/save.
  const [pendingEmailVerification, setPendingEmailVerification] = useState("");

  const changeEmailButtonRef = useRef(null);
  const newEmailFieldRef = useRef(null);
  const emailPanelWasOpenRef = useRef(false);

  const canSave = useMemo(
    () => Boolean(name.trim() && email.trim()) && !isSaving,
    [name, email, isSaving],
  );

  const closeOverlay = () => {
    // Discard/save end the email-change journey — drop the pending hint.
    setPendingEmailVerification("");
    setOverlayName("");
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showSnackbar("Image must be less than 5MB", theme, "error");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setImageToCrop(reader.result);
        setShowCropDialog(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropSave = async () => {
    try {
      const croppedBlob = await getCroppedImg(imageToCrop, croppedAreaPixels);
      const thumbnailBlob = await createThumbnail(croppedBlob);

      // Convert to base64 strings
      const fullBase64 = await blobToBase64(croppedBlob);
      const thumbnailBase64 = await blobToBase64(thumbnailBlob);

      setProfileImage({ full: fullBase64, thumbnail: thumbnailBase64 });
      setProfileImagePreview(fullBase64);
      setShowCropDialog(false);
      setImageToCrop(null);
    } catch (error) {
      console.error("Error cropping image:", error);
      showSnackbar("Error processing image", theme, "error");
    }
  };

  const handleCropCancel = () => {
    setShowCropDialog(false);
    setImageToCrop(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const handleSave = async () => {
    if (isSaving || emailChangeOpen) {
      return;
    }

    if (!name.trim() || !email.trim()) {
      showSnackbar("Name and email are required", theme, "error");
      return;
    }

    setIsSaving(true);
    const imageChanged = Boolean(profileImage?.full);
    const selectedPhoneCountry = countryOptions.find(
      (c) => c.code === phoneCountryCode,
    );
    try {
      await linguistApi.put("/me", {
        Name: name.trim(),
        Email: email.trim(),
        PhoneNumber: phoneNumber.trim(),
        AddressLine1: addressLine1.trim(),
        AddressLine2: addressLine2.trim(),
        AddressLine3: addressLine3.trim(),
        Certifications: certifications.trim(),
        Image: imageChanged
          ? profileImage.full
          : (overlayData?.profile?.Image ?? null),
        ImageThumbnail: imageChanged
          ? profileImage.thumbnail
          : (overlayData?.profile?.ImageThumbnail ?? null),
        PhoneNumbers: phoneNumber.trim()
          ? [
              {
                CountryCode: selectedPhoneCountry
                  ? String(selectedPhoneCountry.callingCode)
                  : "1",
                Number: phoneNumber.trim(),
                Ext: phoneExt.trim() || null,
                IsPrimary: true,
                SortOrder: 0,
              },
            ]
          : [],
      });

      triggerPageRefresh("Profile updated", theme);
      closeOverlay();
    } catch (error) {
      showSnackbar(
        error?.response?.data?.error || "Unable to save profile right now",
        theme,
        "error",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const openEmailChange = useCallback(() => {
    setEmailChangeOpen(true);
    setNewEmail("");
    setNewEmailError("");
    setEmailChangePassword("");
    setShowEmailChangePassword(false);
  }, []);

  const cancelEmailChange = useCallback(() => {
    setEmailChangeOpen(false);
    setNewEmail("");
    setNewEmailError("");
    setEmailChangePassword("");
    setShowEmailChangePassword(false);
  }, []);

  // Y-E3 (focus): the "Change Email" trigger disables itself while the panel
  // is open, so focus would otherwise fall to <body>. Move focus into the
  // New Email field on open and return it to the trigger on close.
  useEffect(() => {
    const wasOpen = emailPanelWasOpenRef.current;
    if (emailChangeOpen && !wasOpen) {
      newEmailFieldRef.current?.focus();
    } else if (!emailChangeOpen && wasOpen) {
      changeEmailButtonRef.current?.focus();
    }
    emailPanelWasOpenRef.current = emailChangeOpen;
  }, [emailChangeOpen]);

  // Y-E1 (progressive dismissal): Overlay.jsx listens for Escape on document
  // (bubble phase). This capture-phase window listener runs first and stops
  // propagation, so Escape closes only the email panel — the shell never
  // receives the event. The next Escape (panel closed) closes the overlay.
  useEffect(() => {
    if (!emailChangeOpen) {
      return undefined;
    }
    const handleEscape = (event) => {
      if (event.key !== "Escape") {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      cancelEmailChange();
    };
    window.addEventListener("keydown", handleEscape, true);
    return () => window.removeEventListener("keydown", handleEscape, true);
  }, [emailChangeOpen, cancelEmailChange]);

  const handleSendVerificationLink = async () => {
    if (isSendingVerification) {
      return;
    }

    const normalizedNewEmail = newEmail.trim();
    if (!normalizedNewEmail) {
      setNewEmailError("New email is required.");
      return;
    }
    if (!EMAIL_PATTERN.test(normalizedNewEmail)) {
      setNewEmailError("Enter a valid email address.");
      return;
    }
    setNewEmailError("");

    if (normalizedNewEmail.toLowerCase() === email.trim().toLowerCase()) {
      showSnackbar("That is already your email address", theme, "info");
      return;
    }

    if (!emailChangePassword) {
      showSnackbar("Enter your current password to change your email", theme, "error");
      return;
    }

    setIsSendingVerification(true);
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        showSnackbar("Your session has expired. Please sign in again.", theme, "error");
        return;
      }

      const credential = EmailAuthProvider.credential(user.email, emailChangePassword);
      await reauthenticateWithCredential(user, credential);
      await verifyBeforeUpdateEmail(user, normalizedNewEmail, {
        url: `${window.location.origin}/loginConfirmation`,
        handleCodeInApp: true,
      });

      showSnackbar(
        `Verification link sent to ${normalizedNewEmail}. Open it to confirm the email change.`,
        theme,
        "success",
      );
      setPendingEmailVerification(normalizedNewEmail);
      setEmailChangeOpen(false);
      setNewEmail("");
      setEmailChangePassword("");
      setShowEmailChangePassword(false);
    } catch (error) {
      console.error("Unable to send email verification link:", error);
      const code = error?.code || "";
      if (code === "auth/requires-recent-login") {
        setEmailChangePassword("");
        showSnackbar(
          "For security, re-enter your current password and try again.",
          theme,
          "error",
        );
      } else if (code === "auth/wrong-password" || code === "auth/invalid-credential") {
        setEmailChangePassword("");
        showSnackbar("Current password is incorrect", theme, "error");
      } else if (code === "auth/invalid-email") {
        setNewEmailError("Enter a valid email address.");
      } else if (code === "auth/email-already-in-use") {
        setNewEmailError("That email address is already in use.");
      } else if (code === "auth/too-many-requests") {
        showSnackbar("Too many attempts. Please try again in a few minutes.", theme, "error");
      } else if (code === "auth/network-request-failed") {
        showSnackbar("Network error. Check your connection and try again.", theme, "error");
      } else {
        showSnackbar("Unable to change email right now. Please try again.", theme, "error");
      }
    } finally {
      setIsSendingVerification(false);
    }
  };

  const footer = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "end",
        alignItems: "center",
        width: "100%",
        flexDirection: "row",
      }}
    >
      {isMobile ? (
        <Tooltip title="Discard" placement="top">
          <IconButton
            color="primary"
            onClick={closeOverlay}
            sx={{
              width: "40px",
              height: "40px",
              border: `2px solid ${theme.palette.primary.main}`,
              borderRadius: "12px",
            }}
          >
            <DisabledByDefaultRoundedIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          color="primary"
          variant="outlined"
          size="large"
          endIcon={<DisabledByDefaultRoundedIcon />}
          onClick={closeOverlay}
          sx={{
            borderRadius: "12px",
          }}
        >
          Discard
        </Button>
      )}

      <Box p="0 .5rem" />

      {isMobile ? (
        <Tooltip
          title={
            isSaving
              ? "Saving..."
              : emailChangeOpen
                ? "Finish or cancel the email change first"
                : "Save"
          }
          placement="top"
        >
          <span>
            <IconButton
              disabled={!canSave || isSaving || emailChangeOpen}
              color="primary"
              onClick={handleSave}
              sx={{
                width: "40px",
                height: "40px",
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.background.default,
                borderRadius: "12px",
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
                "&.Mui-disabled": {
                  backgroundColor: theme.palette.action.disabledBackground,
                  color: theme.palette.action.disabled,
                },
              }}
            >
              <SaveRoundedIcon />
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Tooltip
          title={emailChangeOpen ? "Finish or cancel the email change first" : ""}
          placement="top"
        >
          <span>
            <Button
              loading={isSaving}
              disabled={!canSave || isSaving || emailChangeOpen}
              color="primary"
              variant="contained"
              size="large"
              endIcon={<SaveRoundedIcon />}
              onClick={handleSave}
              sx={{
                borderRadius: "12px",
              }}
            >
              Save
            </Button>
          </span>
        </Tooltip>
      )}
    </Box>
  );

  return (
    <>
      <Overlay
        id="linguist-profile-edit"
        open={true}
        onClose={closeOverlay}
        title="Edit Profile"
        height="fit-content"
        footer={footer}
      >
      <Box
        component="form"
        sx={{
          display: "flex",
          p: "1rem 2.3rem 1rem 2.5rem",
          width: "100%",
          "& .MuiTextField-root": {
            m: 0,
            width: "100%",
          },
        }}
      >
        <Grid container spacing={2} width="100%">
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: getAvatarColor(name),
                  borderRadius: "12px",
                  boxShadow: theme.shadows[3],
                }}
                src={profileImagePreview}
              >
                {getAvatarInitials(name)}
              </Avatar>
              <Box>
                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="linguist-profile-image-input"
                  type="file"
                  onChange={handleImageChange}
                />
                <label htmlFor="linguist-profile-image-input">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CameraAltRoundedIcon />}
                    size="small"
                    sx={{ borderRadius: "8px" }}
                  >
                    Change Photo
                  </Button>
                </label>
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  JPG, PNG, GIF up to 5MB
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              size="small"
              label="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack direction="row" spacing={0.5} alignItems="flex-start">
              <TextField
                size="small"
                label="Email"
                value={email}
                fullWidth
                InputProps={{ readOnly: true }}
                helperText={
                  pendingEmailVerification
                    ? `Verification pending — check your inbox at ${pendingEmailVerification}. Your sign-in email changes after you confirm.`
                    : "Changing your email requires verification."
                }
                sx={{ flex: 1 }}
              />
              <Button
                ref={changeEmailButtonRef}
                variant="outlined"
                size="small"
                onClick={openEmailChange}
                disabled={emailChangeOpen || isSendingVerification}
                sx={{
                  borderRadius: "8px",
                  height: "40px",
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                }}
              >
                Change Email
              </Button>
            </Stack>
          </Grid>
          {emailChangeOpen && (
            <Grid size={{ xs: 12 }}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: "10px" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5 }}>
                  Change Email
                </Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ display: "block", mb: 1.5 }}
                >
                  Enter the new email and confirm your current password. We will send a
                  verification link to the new address.
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      inputRef={newEmailFieldRef}
                      size="small"
                      label="New Email"
                      value={newEmail}
                      onChange={(event) => {
                        setNewEmail(event.target.value);
                        if (newEmailError) {
                          setNewEmailError("");
                        }
                      }}
                      fullWidth
                      error={Boolean(newEmailError)}
                      helperText={newEmailError || ""}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      size="small"
                      label="Current Password"
                      type={showEmailChangePassword ? "text" : "password"}
                      value={emailChangePassword}
                      onChange={(event) => setEmailChangePassword(event.target.value)}
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowEmailChangePassword(!showEmailChangePassword)
                              }
                              edge="end"
                              tabIndex={-1}
                            >
                              {showEmailChangePassword ? (
                                <VisibilityOffIcon />
                              ) : (
                                <VisibilityIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={cancelEmailChange}
                        sx={{ borderRadius: "8px" }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        loading={isSendingVerification}
                        disabled={isSendingVerification}
                        onClick={handleSendVerificationLink}
                        sx={{ borderRadius: "8px" }}
                      >
                        Send Verification Link
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack direction="row" spacing={0.5} alignItems="flex-start">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Country picker — visual recipe mirrored from the admin
                    LinguistNewEdit phone-code select (flag + name + dial code). */}
                <TextField
                  size="small"
                  select
                  label="Code"
                  value={phoneCountryCode}
                  onChange={(event) => setPhoneCountryCode(event.target.value)}
                  fullWidth
                  SelectProps={{
                    renderValue: (selectedCode) => {
                      const selectedCountry = countryOptions.find(
                        (o) => o.code === selectedCode,
                      );
                      if (!selectedCountry) return selectedCode;
                      return (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0, maxWidth: "100%" }}>
                          <img
                            src={selectedCountry.flagUrl}
                            alt={selectedCountry.name}
                            style={{ width: 18, height: "auto", borderRadius: 2, flexShrink: 0 }}
                          />
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {selectedCountry.name}
                          </span>
                          <span style={{ color: "#999", fontSize: 12, flexShrink: 0 }}>
                            +{selectedCountry.callingCode}
                          </span>
                        </span>
                      );
                    },
                    MenuProps: {
                      PaperProps: {
                        sx: { maxHeight: 350 },
                      },
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                    },
                  }}
                >
                  {countryOptions.map((option) => (
                    <MenuItem key={option.code} value={option.code}>
                      <Box
                        component="span"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          width: "100%",
                        }}
                      >
                        <img
                          src={option.flagUrl}
                          alt={option.name}
                          style={{ width: 24, height: "auto", borderRadius: 2, flexShrink: 0 }}
                          loading="lazy"
                        />
                        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {option.name}
                        </span>
                        <span style={{ color: "#999", fontSize: 13, flexShrink: 0 }}>
                          +{option.callingCode}
                        </span>
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <TextField
                size="small"
                label="Phone Number"
                value={phoneNumber}
                onChange={(event) => {
                  const raw = event.target.value.replace(/[^\d+]/g, "");
                  setPhoneNumber(raw);
                }}
                onBlur={(event) => {
                  const raw = event.target.value.replace(/[^\d+]/g, "");
                  if (raw.trim()) {
                    try {
                      const withPlus = /^\+/.test(raw) ? raw : `+${raw}`;
                      const parsed = parsePhoneNumber(withPlus);
                      if (parsed && parsed.isValid()) {
                        setPhoneNumber(parsed.formatNational());
                      }
                    } catch {
                      // ignore
                    }
                  }
                }}
                fullWidth
                sx={{ flex: 1, minWidth: 0 }}
              />
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              size="small"
              label="Ext"
              value={phoneExt || ""}
              onChange={(event) => setPhoneExt(event.target.value)}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              size="small"
              label="Address Line 1"
              value={addressLine1}
              onChange={(event) => setAddressLine1(event.target.value)}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              size="small"
              label="Address Line 2"
              value={addressLine2}
              onChange={(event) => setAddressLine2(event.target.value)}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              size="small"
              label="Address Line 3"
              value={addressLine3}
              onChange={(event) => setAddressLine3(event.target.value)}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              size="small"
              label="Certifications"
              value={certifications}
              onChange={(event) => setCertifications(event.target.value)}
              fullWidth
            />
          </Grid>
        </Grid>
      </Box>
      </Overlay>

      {/* Image Crop Dialog — mirrors the Settings profile photo flow */}
      <Dialog
        open={showCropDialog}
        onClose={(event, reason) => {
          // Prevent closing on backdrop click or escape key
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.default,
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CropRoundedIcon />
            <Typography variant="h4" fontWeight="bold">
              Crop Profile Picture
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, position: "relative", height: 400 }}>
          {imageToCrop && (
            <Cropper
              image={imageToCrop}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              showGrid={false}
              style={{
                containerStyle: {
                  borderRadius: "12px",
                },
              }}
            />
          )}
        </DialogContent>
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            Zoom
          </Typography>
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            onChange={(e, zoomValue) => setZoom(zoomValue)}
            sx={{ mt: 1 }}
          />
        </Box>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={handleCropCancel}
            variant="outlined"
            sx={{ borderRadius: "8px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCropSave}
            variant="contained"
            sx={{ borderRadius: "8px" }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LinguistProfileEditOverlay;
